import React, { useState, useEffect, useRef } from 'react';
import Layout from '../core/Layout';
import { updateUserProfile } from '../actions/userActions'
import { useDispatch, useSelector } from 'react-redux'
import { USERS_UPDATE_PROFILE_RESET } from '../constants/userConstants'
import showToast from '../utils/toast';

const UpdateUsers = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        about: "",
        details: {
            gender: "Male",
            specialisation: "",
            residence: "",
            contact: "",
        },
    });
    
    const getRoleLabel = (role) => {
        switch (role) {
            case 0:
                return 'Admin';
            case 1:
                return 'Doctor';
            case 2:
                return 'Staff';
            default:
                return 'Patient';
        }
    };

    const userLogin = useSelector((state) => state.userLogin)
    const { userInfo: user } = userLogin

    const dispatch = useDispatch()

    const usersUpdate = useSelector((state) => state.usersUpdate)
    const { success: successUpdate } = usersUpdate

    useEffect(() => {
        if (user) {
            console.log("Role", user?.role,"Type", typeof user?.role);
            setFormData({
                ...formData,
                name: user?.name || "",
                email: user?.email || "",
                about: user?.about || "",
                details: {
                    gender: user?.details?.gender || "Male",
                    specialisation: user?.details?.specialisation || "",
                    residence: user?.details?.residence || "",
                    contact: user?.details?.contact || "",
                }
            })
            
            if (user?.profileImage) {
                setImagePreview(user.profileImage);
            }
        }
    }, [user])

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name in formData.details) {
            setFormData({
                ...formData,
                details: { ...formData.details, [name]: value },
            });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageClick = () => {
        if (isEditing) {
            fileInputRef.current.click();
        }
    };

    const submitHandler = (e) => {
        e.preventDefault();
        const updateData = {
            _id: user._id,
            name: formData.name,
            email: formData.email,
            about: formData.about,
            details: formData.details,
            profileImage: profileImage // Add image to update data
        };

        dispatch(updateUserProfile(updateData));
        showToast("profile  is updated")
    };

    const toggleEdit = () => {
        setIsEditing(!isEditing);
    };

    useEffect(() => {
        if (successUpdate) {
            dispatch({ type: USERS_UPDATE_PROFILE_RESET });
            setIsEditing(false);
        }
    }, [dispatch, successUpdate]);

    const styles = {
        container: {
            background: 'whitesmoke',
            minHeight: '100vh',
            paddingTop: '2rem',
            paddingBottom: '2rem',
            width: '100%'
        },
        headerSection: {
            textAlign: 'center',
            marginBottom: '2rem'
        },
        title: {
            color: '#ffffff',
            fontSize: '2.8rem',
            fontWeight: '800',
            marginBottom: '0.5rem',
            textShadow: '0 4px 8px rgba(0,0,0,0.3)',
            letterSpacing: '1px'
        },
        subtitle: {
            color: 'rgba(255,255,255,0.9)',
            fontSize: '1.1rem',
            fontWeight: '400',
            marginBottom: '2rem'
        },
        card: {
            borderRadius: '30px',
            border: 'none',
            boxShadow: '0 25px 60px rgba(0,0,0,0.15), 0 10px 30px rgba(0,0,0,0.1)',
            backgroundColor: '#ffffff',
            padding: '0',
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%'
        },
        cardHeader: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '2.5rem',
            borderRadius: '30px 30px 0 0',
            color: 'white',
            position: 'relative'
        },
        cardHeaderContent: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
        },
        cardHeaderLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
        },
        profileImageContainer: {
            position: 'relative',
            width: '120px', 
            height: '120px', 
            borderRadius: '50%',
            overflow: 'visible', 
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: '3px solid rgba(255,255,255,0.3)',
            cursor: isEditing ? 'pointer' : 'default',
            transition: 'all 0.3s ease',
            '&:hover': {
                transform: isEditing ? 'scale(1.05)' : 'none'
            }
        },
        profileImage: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%'
        },
        profileImagePlaceholder: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '3rem', 
            color: 'white',
            borderRadius: '50%',
            overflow: 'hidden'
        },
        addImageOverlay: {
            position: 'absolute',
            bottom: '8px', 
            right: '8px', 
            width: '36px', 
            height: '36px', 
            backgroundColor: '#667eea',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px', 
            fontWeight: 'bold',
            color: 'white',
            border: '3px solid white',
            cursor: 'pointer',
            opacity: isEditing ? 1 : 0,
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            zIndex: 10
        },
        hiddenFileInput: {
            display: 'none'
        },
        cardHeaderIcon: {
            width: '60px',
            height: '60px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.8rem'
        },
        cardHeaderText: {
            flex: '1'
        },
        cardHeaderTitle: {
            fontSize: '1.8rem',
            fontWeight: '700',
            margin: '0 0 0.5rem 0',
            color: 'white'
        },
        cardHeaderSubtitle: {
            fontSize: '1rem',
            margin: '0',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: '400'
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap'
        },
        editButton: {
            background: isEditing 
                ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)' 
                : 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
            border: 'none',
            borderRadius: '25px',
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: '600',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isEditing 
                ? '0 8px 25px rgba(255, 107, 107, 0.4)' 
                : '0 8px 25px rgba(78, 205, 196, 0.4)',
            textTransform: 'none',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            minWidth: '100px'
        },
        updateButton: {
            background: isEditing 
                ? 'linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%)' 
                : 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)',
            border: 'none',
            borderRadius: '25px',
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: '600',
            color: isEditing ? '#2d3436' : '#636e72',
            cursor: isEditing ? 'pointer' : 'not-allowed',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isEditing 
                ? '0 8px 25px rgba(168, 230, 207, 0.4)' 
                : '0 4px 15px rgba(149, 165, 166, 0.2)',
            textTransform: 'none',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            minWidth: '120px',
            opacity: isEditing ? 1 : 0.7
        },
        formContainer: {
            padding: '3rem'
        },
        formRow: {
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr', 
            gap: '2rem',
            marginBottom: '0'
        },
        formGroup: {
            marginBottom: '2rem'
        },
        formLabel: {
            fontWeight: '700',
            color: '#2d3436',
            marginBottom: '0.8rem',
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        labelIcon: {
            fontSize: '1rem',
            color: '#667eea'
        },
        formControl: {
            borderRadius: '15px',
            border: isEditing ? '2px solid #e3f2fd' : '2px solid #f0f0f0',
            padding: '1.2rem 1.5rem',
            fontSize: '1rem',
            width: '100%',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            backgroundColor: isEditing ? '#ffffff' : '#f8f9fa',
            fontFamily: 'inherit',
            boxShadow: isEditing ? '0 4px 12px rgba(102, 126, 234, 0.08)' : '0 2px 8px rgba(0,0,0,0.04)',
            color: isEditing ? '#2d3436' : '#636e72',
            outline: 'none'
        },
        formControlDisabled: {
            backgroundColor: '#f1f3f4',
            color: '#6c757d',
            cursor: 'not-allowed',
            opacity: '0.8'
        },
        textareaLarge: {
            minHeight: '200px',
            resize: 'vertical',
            gridColumn: '1 / -1'
        },
        aboutSection: {
            gridColumn: '1 / -1',
            marginTop: '1rem'
        },
        readOnlyBadge: {
            position: 'absolute',
            right: '15px',
            top: '15px',
            background: '#e9ecef',
            color: '#6c757d',
            fontSize: '0.75rem',
            padding: '4px 8px',
            borderRadius: '12px',
            fontWeight: '500'
        }
    };

    return (
        <Layout>
            <div style={styles.container}>
                <div className="container-fluid px-4">
    
                    <style jsx>{`
                        .form-control-focus:focus {
                            border-color: #667eea !important;
                            box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15), 0 8px 25px rgba(102, 126, 234, 0.2) !important;
                            transform: translateY(-2px);
                        }
                        
                        .form-control-focus:focus:not(:disabled) {
                            background-color: #ffffff !important;
                            color: #2d3436 !important;
                        }
                        
                        .form-control-focus:hover:not(:disabled) {
                            border-color: #a8b3f0 !important;
                            box-shadow: 0 6px 20px rgba(102, 126, 234, 0.12) !important;
                        }
                        
                        .form-control-focus {
                            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                        }
                        
                        .form-control-disabled:focus {
                            box-shadow: 0 0 0 4px rgba(149, 165, 166, 0.1) !important;
                            border-color: #bdc3c7 !important;
                        }

                        .profile-image-hover:hover .add-overlay {
                            transform: scale(1.1);
                            background-color: #5a67d8;
                        }
                        
                        .profile-image-hover:hover {
                            transform: scale(1.06);
                        }
                        
                        .upload-hint {
                            position: absolute;
                            bottom: -35px; /* Adjusted for larger circle */
                            left: 50%;
                            transform: translateX(-50%);
                            font-size: 0.75rem;
                            color: rgba(255,255,255,0.8);
                            white-space: nowrap;
                            opacity: 0;
                            transition: opacity 0.3s ease;
                        }
                        
                        .profile-image-hover:hover .upload-hint {
                            opacity: 1;
                        }
                    `}</style>
                    
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <div className="card" style={styles.card}>
                                <div style={styles.cardHeader}>
                                    <div style={styles.cardHeaderContent}>
                                        <div style={styles.cardHeaderLeft}>
                                            <div 
                                                style={styles.profileImageContainer}
                                                className="profile-image-hover"
                                                onClick={handleImageClick}
                                                title={isEditing ? "Click to upload image" : ""}
                                            >
                                                {imagePreview ? (
                                                    <img 
                                                        src={imagePreview} 
                                                        alt="Profile" 
                                                        style={styles.profileImage}
                                                    />
                                                ) : (
                                                    <div style={styles.profileImagePlaceholder}>
                                                        👤
                                                    </div>
                                                )}
                                                {isEditing && (
                                                    <>
                                                        <div 
                                                            style={styles.addImageOverlay}
                                                            className="add-overlay"
                                                        >
                                                            +
                                                        </div>
                                                        <div className="upload-hint">
                                                            Click to upload
                                                        </div>
                                                    </>
                                                )}
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                    style={styles.hiddenFileInput}
                                                />
                                            </div>
                                            <div style={styles.cardHeaderText}>
                                                <h2 style={styles.cardHeaderTitle}>Profile Details</h2>
                                                <p style={styles.cardHeaderSubtitle}>Update your professional information</p>
                                            </div>
                                        </div>
                                        <div style={styles.buttonGroup}>
                                            <button 
                                                onClick={toggleEdit}
                                                style={styles.editButton}
                                            >
                                                ✏️ {isEditing ? 'Cancel' : 'Edit'}
                                            </button>
                                            <button 
                                                onClick={submitHandler}
                                                disabled={!isEditing}
                                                style={styles.updateButton}
                                            >
                                                💾 Update Profile
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={styles.formContainer}>
                                    <div style={styles.formRow}>
                                        <div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    <span style={styles.labelIcon}>👤</span>
                                                    Full Name
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name="name" 
                                                    value={formData.name} 
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    className={isEditing ? 'form-control-focus' : 'form-control-disabled'}
                                                    style={{
                                                        ...styles.formControl,
                                                        ...(isEditing ? {} : styles.formControlDisabled)
                                                    }}
                                                    required
                                                />
                                            </div>
                                            
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    <span style={styles.labelIcon}>📧</span>
                                                    Email Address
                                                </label>
                                                <div style={{position: 'relative'}}>
                                                    <input 
                                                        type="email" 
                                                        name="email" 
                                                        value={formData.email} 
                                                        disabled={true}
                                                        className="form-control-disabled"
                                                        style={{
                                                            ...styles.formControl,
                                                            ...styles.formControlDisabled
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    <span style={styles.labelIcon}>⚧</span>
                                                    Gender
                                                </label>
                                                <select 
                                                    name="gender" 
                                                    value={formData.details.gender} 
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    className={isEditing ? 'form-control-focus' : 'form-control-disabled'}
                                                    style={{
                                                        ...styles.formControl,
                                                        ...(isEditing ? {} : styles.formControlDisabled)
                                                    }}
                                                >
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    <span style={styles.labelIcon}>📞</span>
                                                    Contact
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name="contact" 
                                                    value={formData.details.contact} 
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    className={isEditing ? 'form-control-focus' : 'form-control-disabled'}
                                                    placeholder='Enter Your Phone Number...'
                                                    style={{
                                                        ...styles.formControl,
                                                        ...(isEditing ? {} : styles.formControlDisabled)
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    <span style={styles.labelIcon}>💼</span>
                                                    Role
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name="role" 
                                                    value={getRoleLabel(user.role)} 
                                                    onChange={handleChange}
                                                    disabled={true}
                                                    className={isEditing ? 'form-control-focus' : 'form-control-disabled'}
                                                    style={{
                                                        ...styles.formControl,
                                                        ...(styles.formControlDisabled)
                                                    }}
                                                />
                                            </div>
                                            
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    <span style={styles.labelIcon}>🩺</span>
                                                    Specialisation
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name="specialisation" 
                                                    value={formData.details.specialisation} 
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    className={isEditing ? 'form-control-focus' : 'form-control-disabled'}
                                                    placeholder='eg General Physician, Pediatrics, Cardiology'
                                                    style={{
                                                        ...styles.formControl,
                                                        ...(isEditing ? {} : styles.formControlDisabled)
                                                    }}
                                                />
                                            </div>
                                            
                                            <div style={styles.formGroup}>
                                                <label style={styles.formLabel}>
                                                    <span style={styles.labelIcon}>📍</span>
                                                    Residence
                                                </label>
                                                <input 
                                                    type="text" 
                                                    name="residence" 
                                                    value={formData.details.residence} 
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    className={isEditing ? 'form-control-focus' : 'form-control-disabled'}
                                                    placeholder='City, State'
                                                    style={{
                                                        ...styles.formControl,
                                                        ...(isEditing ? {} : styles.formControlDisabled)
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div style={styles.aboutSection}>
                                        <div style={styles.formGroup}>
                                            <label style={styles.formLabel}>
                                                <span style={styles.labelIcon}>📝</span>
                                                About
                                            </label>
                                            <textarea 
                                                name="about" 
                                                value={formData.about} 
                                                onChange={handleChange}
                                                disabled={!isEditing}
                                                className={isEditing ? 'form-control-focus' : 'form-control-disabled'}
                                                style={{
                                                    ...styles.formControl,
                                                    ...styles.textareaLarge,
                                                    ...(isEditing ? {} : styles.formControlDisabled)
                                                }}
                                                rows="6"
                                                placeholder="Tell us about yourself and your professional experience..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}

export default UpdateUsers;